import React from "react";
import { useCallback, useState, useEffect } from "react";
import { isAdmin } from "../../utils/roles";
import './ride.css';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faTrashCan, faPlay, faCircleCheck, faCircleXmark, faCheck, faXmark, faFlag, faCar, faCreditCard, faPenToSquare, faRotate, faShareNodes, faTriangleExclamation, faComment } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import toast from 'react-hot-toast';
import RouteMap from '../routeMap/RouteMap';
import Chat from '../chat/Chat';
import { joinRideRoom, leaveRideRoom, onSocketEvent, offSocketEvent } from '../../services/socket';
import '../payment/payment.css';
import useTour from "../../tour/useTour";
import rideSteps from "../../tour/steps/rideSteps";
import TourButton from "../tourButton/TourButton";
import { hapticLight, hapticSuccess, hapticWarning, hapticHeavy } from "../../services/haptics";


const Ride = ( { isLoggedIn, loginUser, onLogout } ) => {
  const { rideId } = useParams();
  const [rideDetails, setRideDetails] = useState(null);
  const [deleteInProcess, setDeleteInProcess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDescription, setReportDescription] = useState("");
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);
  const [passengerReviews, setPassengerReviews] = useState({});
  // reviewee userID -> rating, from the server — survives refreshes, unlike
  // the optimistic `passengerReviews[..].submitted` flags.
  const [myRideReviews, setMyRideReviews] = useState({});
  // Driver's per-passenger boarding-code entries (userID -> typed code).
  const [boardingInputs, setBoardingInputs] = useState({});
  const [liveLocation, setLiveLocation] = useState(null);
  const [bookSeats, setBookSeats] = useState(1);
  const navigate = useNavigate();

  const rideUrl = `/api/v1/ride/getOneRide/${rideId}`;

  const getSingleRide = useCallback (async () => {
    try {
      const response = await axios.get(rideUrl);
      setRideDetails(response.data.rideWithPosterPassengerInfo);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      }
    }
  }, [rideUrl, onLogout, navigate]);

  useEffect(() => {
    getSingleRide();
    joinRideRoom(rideId);

    const handleRideUpdated = () => {
      getSingleRide();
    };

    onSocketEvent("ride-updated", handleRideUpdated);
    const fallbackInterval = setInterval(getSingleRide, 60000);

    return () => {
      leaveRideRoom(rideId);
      offSocketEvent("ride-updated", handleRideUpdated);
      clearInterval(fallbackInterval);
    };
  }, [getSingleRide, rideId]);

  // Live tracking: derive role/transit from the loaded ride (primitive deps keep
  // the streaming effect from re-running on every unrelated ride refresh).
  const amDriver = rideDetails?.poster?._id === loginUser._id;
  const rideInTransit = !!rideDetails?.isStarted && !rideDetails?.isCompleted;

  // Seed the live marker from the ride's last known position (for late joiners).
  useEffect(() => {
    const cl = rideDetails?.currentLocation;
    if (cl && Number.isFinite(cl.lat) && Number.isFinite(cl.lng)) {
      setLiveLocation((prev) => prev || { lat: cl.lat, lng: cl.lng, at: cl.updatedAt });
    }
  }, [rideDetails]);

  // Once the ride is completed, load which reviews I already submitted so a
  // page refresh shows "Reviewed" instead of an empty form (the server rejects
  // duplicates anyway — this just keeps the UI honest).
  const rideCompleted = !!rideDetails?.isCompleted;
  useEffect(() => {
    if (!rideCompleted) return;
    axios.get(`/api/v1/review/mine/${rideId}`)
      .then((res) => {
        const map = {};
        (res.data.reviews || []).forEach((r) => { map[r.reviewee] = r.rating; });
        setMyRideReviews(map);
      })
      .catch(() => {});
  }, [rideCompleted, rideId]);

  // Passengers/viewers: listen for the driver's live position over the socket.
  useEffect(() => {
    const handleDriverLocation = (data) => {
      if (!data || String(data.rideId) !== String(rideId)) return;
      setLiveLocation({ lat: data.lat, lng: data.lng, at: data.at });
    };
    onSocketEvent("driver-location", handleDriverLocation);
    return () => offSocketEvent("driver-location", handleDriverLocation);
  }, [rideId]);

  // Driver of an in-transit ride: stream GPS to passengers (throttled to ~10s).
  useEffect(() => {
    if (!amDriver || !rideInTransit || !navigator.geolocation) return;
    let lastSent = 0;
    const post = (pos) => {
      const now = Date.now();
      if (now - lastSent < 10000) return;
      lastSent = now;
      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setLiveLocation(coords); // show the driver their own position immediately
      axios.post(`/api/v1/ride/${rideId}/location`, coords).catch(() => {});
    };
    const watchId = navigator.geolocation.watchPosition(post, () => {}, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });
    return () => navigator.geolocation.clearWatch(watchId);
  }, [amDriver, rideInTransit, rideId]);

  const { startTour } = useTour("ride", rideSteps, !loading && rideDetails !== null);

  if (!isLoggedIn) {
    navigate('/');
    return null;
  }

  if (loading || deleteInProcess) {
    return (
      <div className="sr-ride">
        <div className="sr-ride__top">
          <div className="sr-ride__top-content">
            <div className="sr-ride__status">
              <div className="sr-ride__spinner" />
              <span className="sr-ride__status-text">{deleteInProcess ? "Deleting ride..." : "Loading..."}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { _id, arrival, departure, route, estimatedDate, isStarted, totalSeats, isOfferOrRequest, fare = {}, likes = [], poster = {}, passengerList = [], departureCoords, arrivalCoords, isImported, whatsappContact, posterName, claimStatus, isWomenOnly } = rideDetails;

  const isLikedByUser = Array.isArray(likes) && likes.some(id => id === loginUser._id);
  const isPoster = poster._id === loginUser._id;
  const isPassenger = passengerList.some(el => el.userID === loginUser._id);
  // Boarding gate: passengers still neither code-verified nor marked no-show.
  // The server refuses Complete while this is non-zero.
  const unresolvedBoarding = passengerList.filter((p) => p.hasBoardingCode && p.attendanceStatus === "unverified").length;
  // Gender is stored as the canonical "female" string or the legacy "0" numeric code.
  const isFemaleUser = ["female", "0"].includes(String(loginUser?.gender ?? "").trim().toLowerCase());
  const blockedByWomenOnly = !!isWomenOnly && !isFemaleUser;
  const hasVehicle = isOfferOrRequest && rideDetails.vehicle && (rideDetails.vehicle.model || rideDetails.vehicle.color || rideDetails.vehicle.plateNumber);

  // Imported (WhatsApp) ride helpers.
  //
  // A claimed ride keeps isImported:true (see server/utils/claims.js — flipping it
  // would make it count toward the owner's one-active-offer limit). So ownership
  // is expressed by claimStatus, and the UI must key off that rather than off
  // isImported, or a verified owner's ride would keep rendering as an anonymous
  // scraped post.
  // A claimed/attributed ride keeps isImported:true on the server (ownership is
  // carried by claimStatus, not the flag — see server/utils/claims.js). Keying the
  // "anonymous scraped post" UI off claimStatus means a ride auto-linked to its
  // owner renders as a normal ride with their profile and badges.
  const isClaimed = claimStatus === "claimed";
  const isImportedRide = !!isImported && !isClaimed;
  const driverDisplayName = (isImportedRide && posterName) ? posterName : poster.userName;
  const waNumber = (whatsappContact || "").replace(/[^\d]/g, "");

  const openWhatsAppContact = () => {
    const text = `Hi! I saw your ride (${departure} → ${arrival}) on SastiRide. Is it still available?`;
    const base = waNumber ? `https://wa.me/${waNumber}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const getStatusInfo = () => {
    // Prefer the server-derived status (utils/rideStatus.js) — it knows the
    // closed states (expired/fulfilled/deleted) the raw flags can't distinguish.
    switch (rideDetails.status) {
      case "expired": return { label: "Expired", cls: "sr-ride__status-badge--completed" };
      case "fulfilled": return { label: "Fulfilled", cls: "sr-ride__status-badge--completed" };
      case "deleted": return { label: "Cancelled", cls: "sr-ride__status-badge--completed" };
      case "completed": return { label: "Completed", cls: "sr-ride__status-badge--completed" };
      case "in_transit": return { label: "In Transit", cls: "sr-ride__status-badge--started" };
      case "past_due": return { label: "Past departure", cls: "sr-ride__status-badge--completed" };
      case "upcoming": return { label: "Available", cls: "sr-ride__status-badge--available" };
      default: break;
    }
    // Fallback for responses without the derived field.
    if (rideDetails.isCompleted) return { label: "Completed", cls: "sr-ride__status-badge--completed" };
    if (isStarted) return { label: "In Transit", cls: "sr-ride__status-badge--started" };
    return { label: "Available", cls: "sr-ride__status-badge--available" };
  };

  const statusInfo = getStatusInfo();

  const onBtnClickLikeRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => {
        const alreadyLiked = Array.isArray(prevDetails.likes) && prevDetails.likes.some(id => id === loginUser._id);
        return {
          ...prevDetails,
          likes: alreadyLiked
            ? prevDetails.likes.filter(id => id !== loginUser._id)
            : [...(prevDetails.likes || []), loginUser._id],
        };
      });

      const response = await axios.post(`/api/v1/ride/likeRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        likes: response.data.isLiked
          ? [...new Set([...(prevDetails.likes || []), loginUser._id])]
          : (prevDetails.likes || []).filter(id => id !== loginUser._id),
      }));
      hapticLight();

    } catch (error) {
      getSingleRide();
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to update like.");
      }
    }
  };

  const handleBookRide = async (rideID) => {
    // Clamp to what's available in case state changed since render.
    const seats = Math.min(Math.max(1, bookSeats), totalSeats);
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats - seats,
        passengerList: [
          ...passengerList, {
            userID: "Loading",
            name: "Loading...",
            ride: rideID,
            seatsBooked: seats,
          }
        ]
      }));

      const response = await axios.post(`/api/v1/ride/addPassenger/${rideID}`, { seats });

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: response.data.updatedTotalSeats,
        passengerList: [
          ...passengerList,
          response.data.newPassenger
        ]
      }));
      setLoading(false);
      hapticSuccess();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats + seats,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== "Loading"
        ),
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to book ride.");
      }
    }
  };

  // Share the user's own trip (route, driver, time, link) with family for safety.
  const handleShareTrip = async () => {
    if (!rideDetails) return;
    const when = new Date(rideDetails.estimatedDate).toLocaleString();
    const driver = (rideDetails.isImported && rideDetails.posterName) ? rideDetails.posterName : rideDetails.poster?.userName;
    const text = `I'm taking a SastiRide from ${rideDetails.departure} to ${rideDetails.arrival} on ${when}. Driver: ${driver || "TBD"}. Follow my trip here: ${window.location.href}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My SastiRide trip", text });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      }
    } catch (e) {
      // User dismissed the share sheet — nothing to do.
    }
  };

  // Marketing share of the ride listing. Prefers the native share sheet (which
  // exposes WhatsApp/Facebook/copy on mobile + the Capacitor app), falling back
  // to copy-to-clipboard, then the WhatsApp web link.
  const handleShareRide = async () => {
    if (!rideDetails) return;
    const text = `Check out this ride on SastiRide!\n${rideDetails.departure} → ${rideDetails.arrival}\n${window.location.href}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${rideDetails.departure} → ${rideDetails.arrival} | SastiRide`, text, url: window.location.href });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied!");
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      }
    } catch (e) {
      // User dismissed the share sheet — nothing to do.
    }
  };

  const sendSos = async (coords) => {
    try {
      hapticHeavy();
      const res = await axios.post("/api/v1/user/sos", { rideId, lat: coords?.lat, lng: coords?.lng });
      const { emergencyContacts = [], contactMessage } = res.data;
      toast.success(res.data.message || "🆘 SOS sent.");
      if (emergencyContacts.length > 0) {
        const phone = String(emergencyContacts[0].phone).replace(/[^\d]/g, "");
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(contactMessage)}`, "_blank");
      } else {
        toast("Add emergency contacts in your profile to alert them automatically.", { icon: "⚠️" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate("/login");
      } else {
        toast.error(error.response?.data?.message || "Failed to send SOS.");
      }
    }
  };

  // SOS: confirm, grab a best-effort location, then alert admins + trusted contacts.
  const handleSos = () => {
    if (!window.confirm("Send an emergency SOS? Admins and your emergency contacts will be alerted with your location.")) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => sendSos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => sendSos(null), // permission denied / unavailable — send without coords
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      sendSos(null);
    }
  };

  const handleCancelBooking = async (rideID, passengerID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats + 1,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== passengerID
        ),
      }));

      const response = await axios.post(`/api/v1/ride/deletePassenger/${rideID}&${passengerID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: response.data.updatedTotalSeats,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== response.data.deletedPassengerID
        ),
      }));
      setLoading(false);
      hapticWarning();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats - 1,
        passengerList: passengerList,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to cancel booking.");
      }
    }
  };

  const handleToggleAvailed = async (rideID, passengerID) => {
    // Send the explicit desired state so a double-tap or second device can't
    // flip the mark to the opposite of what was chosen.
    const current = passengerList.find((p) => p.userID === passengerID)?.isRideAvailed ?? false;
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) => passenger.userID === passengerID
          ? {...passenger, isRideAvailed: !passenger.isRideAvailed}
          : passenger
        )
      }));

      const response = await axios.post(`/api/v1/ride/toggleAvailed/${rideID}&${passengerID}?_method=PUT`, { availed: !current });

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) =>  passenger.userID === passengerID
          ? {...passenger, isRideAvailed: response.data.updatedAvailedMark}
          : passenger
        )
      }));
      setLoading(false);
      hapticLight();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) => passenger.userID === passengerID
          ? {...passenger, isRideAvailed: !passenger.isRideAvailed}
          : passenger
        )
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to update passenger.");
      }
    }
  };

  const onBtnClickStartRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: true,
      }));

      const response = await axios.post(`/api/v1/ride/startRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: response.data.isStarted,
      }));
      setLoading(false);
      hapticHeavy();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: false,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to start ride.");
      }
    }
  };

  const handleAcceptRequest = async (rideID) => {
    if (!window.confirm("Accept this request? We'll create your ride for this trip at the requester's fare and book them onto it.")) return;
    try {
      const res = await axios.post(`/api/v1/ride/acceptRequest/${rideID}`);
      toast.success("Request accepted — your ride is created!");
      hapticSuccess();
      navigate(`/ride/${res.data.newRideId}`);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to accept request.");
      }
    }
  };

  const onBtnClickUnstartRide = async (rideID) => {
    try {
      await axios.post(`/api/v1/ride/unstartRide/${rideID}?_method=PUT`);
      toast.success("Ride start undone.");
      hapticWarning();
      getSingleRide();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to undo start.");
      }
    }
  };

  const onBtnClickCompleteRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: true,
      }));

      const response = await axios.post(`/api/v1/ride/completeRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: response.data.isCompleted,
      }));
      hapticSuccess();
    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: false,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to complete ride.");
      }
    }
  };

  const onBtnClickDeleteRide = async (rideID) => {
    const activePassengers = passengerList.filter(p => !p.isBookingCancelled);
    if (activePassengers.length > 0) {
      const confirmed = window.confirm(
        `This ride has ${activePassengers.length} booked passenger${activePassengers.length > 1 ? 's' : ''}. Cancelling will notify them and affect your reliability score. Continue?`
      );
      if (!confirmed) return;
    }

    try {
      setDeleteInProcess(true);
      await axios.post(`/api/v1/ride/deleteRide/${rideID}?_method=PUT`);
      setDeleteInProcess(false);
      hapticWarning();
      navigate('/profile');
    } catch (error) {
      setDeleteInProcess(false);
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to delete ride.");
      }
    }
  };

  // Driver types the code the passenger tells them at pickup — a match is
  // co-presence proof and the only way a booking becomes attendance-verified.
  const handleVerifyBoarding = async (rideID, passengerID) => {
    const code = (boardingInputs[passengerID] || "").trim();
    if (!code) { toast.error("Enter the passenger's boarding code."); return; }
    try {
      await axios.post(`/api/v1/ride/verifyBoarding/${rideID}&${passengerID}?_method=PUT`, { code });
      toast.success("Boarding verified!");
      hapticSuccess();
      setRideDetails((prev) => ({
        ...prev,
        passengerList: prev.passengerList.map((p) =>
          p.userID === passengerID ? { ...p, attendanceStatus: "verified", isRideAvailed: true } : p
        ),
      }));
      setBoardingInputs((prev) => ({ ...prev, [passengerID]: "" }));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to verify boarding.");
      }
    }
  };

  const handleConfirmCompletion = async (confirmed) => {
    try {
      setConfirmingCompletion(true);
      await axios.post(`/api/v1/ride/confirmCompletion/${_id}?_method=PUT`, { confirmed });
      setRideDetails((prev) => ({
        ...prev,
        passengerList: prev.passengerList.map((p) =>
          p.userID === loginUser._id
            ? { ...p, hasConfirmedCompletion: confirmed ? "confirmed" : "disputed" }
            : p
        ),
      }));
      toast.success(confirmed ? "Arrival confirmed!" : "Dispute recorded.");
      confirmed ? hapticSuccess() : hapticWarning();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to record response.");
      }
    } finally {
      setConfirmingCompletion(false);
    }
  };


  return (
    <div className="sr-ride">
      <Helmet>
        <title>{`${departure} to ${arrival} - ${isOfferOrRequest ? "Ride Offer" : "Ride Request"} | SastiRide`}</title>
        <meta name="description" content={`${isOfferOrRequest ? "Ride offer" : "Ride request"} from ${departure} to ${arrival}${route ? ` via ${route}` : ""}. ${totalSeats} seats, Rs. ${fare.amount || "N/A"} per seat on SastiRide.`} />
        <link rel="canonical" href={`https://sastiride.com/ride/${_id}`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* ── Dark top section ── */}
      <div className="sr-ride__top">
        <div className="sr-ride__top-content">

          {/* ── Type badge only ── */}
          <div className="sr-ride__hero">
            <span className={`sr-ride__type-badge ${isOfferOrRequest ? 'sr-ride__type-badge--offer' : 'sr-ride__type-badge--request'}`}>
              {isOfferOrRequest ? "Ride Offer" : "Ride Request"}
            </span>
            {isImportedRide && (
              <span className="sr-ride__type-badge sr-ride__type-badge--whatsapp">
                <FontAwesomeIcon icon={faWhatsapp} /> From WhatsApp
              </span>
            )}
            {isWomenOnly && (
              <span className="sr-ride__type-badge sr-ride__type-badge--women">
                Women only
              </span>
            )}
            {isOfferOrRequest && rideDetails.preferences && [
              rideDetails.preferences.ac && "AC",
              rideDetails.preferences.luggage && "Luggage space",
              rideDetails.preferences.flexibleTime && "Flexible timing",
            ].filter(Boolean).map((label) => (
              <span key={label} className="sr-ride__type-badge sr-ride__type-badge--pref">
                {label}
              </span>
            ))}
          </div>

        </div>
      </div>

      {/* ── White body section ── */}
      <div className="sr-ride__body">

        {/* ── Combined route + stats card ── */}
        <div className="sr-ride__card" data-tour="ride-info">
          {/* Status strip at top of card */}
          <div className={`sr-ride__status-strip ${statusInfo.cls}`}>
            {statusInfo.label}
          </div>

          {/* Row 1: From → To side by side */}
          <div className="sr-ride__route-row">
            <div className="sr-ride__route-col">
              <span className="sr-ride__route-label">From</span>
              <span className="sr-ride__route-city">{departure}</span>
            </div>
            <div className="sr-ride__route-arrow">
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                <path d="M0 6h17M13 1l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="sr-ride__route-col sr-ride__route-col--to">
              <span className="sr-ride__route-label">To</span>
              <span className="sr-ride__route-city">{arrival}</span>
            </div>
          </div>
          {route && route !== "nil" && (
            <div className="sr-ride__route-via">via {route}</div>
          )}

          {/* Row 2: Stats (3 columns only) */}
          <div className="sr-ride__stats">
            <div className="sr-ride__stat">
              <span className="sr-ride__stat-label">Departure</span>
              <span className="sr-ride__stat-value">
                {new Date(estimatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                {', '}
                {new Date(estimatedDate).toLocaleTimeString([], { hour: '2-digit', hour12: true, timeZone: 'UTC' })}
              </span>
            </div>

            {/* Offers: the driver's price. Requests: what the requester offers to pay. */}
            <div className="sr-ride__stat">
              <span className="sr-ride__stat-label">{isOfferOrRequest ? "Fare" : "Offering"}</span>
              <span className="sr-ride__stat-value">{`${fare?.currency || "PKR"} ${fare?.amount ?? "—"}`}</span>
            </div>

            {isOfferOrRequest && (
              <div className="sr-ride__stat">
                <span className="sr-ride__stat-label">Seats</span>
                <span className="sr-ride__stat-value">{totalSeats}</span>
              </div>
            )}
          </div>

          {/* Poster + Vehicle inside same card */}
          <div className="sr-ride__card-footer">
            {isImportedRide ? (
              <div className="sr-ride__poster-link">
                <img src={poster.avatar} alt="" className="sr-ride__poster-avatar" />
                <div className="sr-ride__poster-details">
                  <span className="sr-ride__poster-name">{driverDisplayName || "WhatsApp post"}</span>
                  <span className="sr-ride__poster-badge sr-ride__poster-badge--whatsapp">
                    <FontAwesomeIcon icon={faWhatsapp} /> From WhatsApp
                  </span>
                </div>
              </div>
            ) : (
              <Link to={`/profile/${poster._id}`} className="sr-ride__poster-link">
                <img
                  src={poster.avatar}
                  alt={poster.userName}
                  className="sr-ride__poster-avatar"
                />
                <div className="sr-ride__poster-details">
                  <span className="sr-ride__poster-name">{poster.userName}</span>
                  {isAdmin(poster) && (
                    <span className="sr-ride__poster-badge sr-ride__poster-badge--admin">Admin</span>
                  )}
                  {poster.isApproved && !isAdmin(poster) && (
                    <span className="sr-ride__poster-badge sr-ride__poster-badge--verified">Admin Verified</span>
                  )}
                  {poster.verificationStatus === "verified" && (
                    <span className="sr-ride__poster-badge sr-ride__poster-badge--id-verified">ID Verified</span>
                  )}
                  {poster.whatsappVerified && (
                    <span className="sr-ride__poster-badge sr-ride__poster-badge--whatsapp-verified">
                      <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Verified
                    </span>
                  )}
                </div>
              </Link>
            )}
            {hasVehicle && (
              <div className="sr-ride__vehicle-info">
                <FontAwesomeIcon icon={faCar} className="sr-ride__vehicle-icon" />
                <span className="sr-ride__vehicle-text">
                  {[rideDetails.vehicle.model, rideDetails.vehicle.color, rideDetails.vehicle.plateNumber].filter(Boolean).join(" \u00B7 ")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Route Map ── */}
        {departureCoords && arrivalCoords && departureCoords.lat && arrivalCoords.lat && (
          <div className="sr-ride__map-wrapper" data-tour="ride-map">
            {isStarted && !rideDetails.isCompleted && liveLocation && (
              <div className="sr-ride__live-indicator">
                <span className="sr-ride__live-dot" /> Live location {amDriver ? "— sharing your trip" : "— driver in transit"}
              </div>
            )}
            <RouteMap
              departureCoords={departureCoords}
              arrivalCoords={arrivalCoords}
              departureName={departure}
              arrivalName={arrival}
              liveLocation={isStarted && !rideDetails.isCompleted ? liveLocation : null}
            />
          </div>
        )}

        {/* ── Chat (ride members only) ── */}
        {(isPoster || isPassenger) && (
          <div data-tour="ride-chat">
            <Chat rideId={_id} loginUser={loginUser} />
          </div>
        )}

        {/* ── Boarding code (minted at Start; visible only to its passenger).
               One code covers both sides: the driver typing it in verifies the
               passenger's attendance AND stands as their arrival confirmation
               unless they later dispute. ── */}
        {!isPoster && isPassenger && rideDetails.myBoardingCode && (() => {
          const myRecord = passengerList.find((el) => el.userID === loginUser._id);
          if (myRecord && myRecord.attendanceStatus === "verified") return null;
          return (
            <div className="sr-ride__confirmation">
              <h3 className="sr-ride__section-title">Your boarding code</h3>
              <div className="sr-ride__claim-code">{rideDetails.myBoardingCode}</div>
              <p className="sr-ride__confirmation-text">
                Tell this to the driver when you board — it verifies your ride for both of you.
                Nothing else to do afterwards.
              </p>
            </div>
          );
        })()}

        {/* ── Passengers ── */}
        {isOfferOrRequest && passengerList.length > 0 && (
          <div className="sr-ride__passengers" data-tour="ride-passengers">
            <h3 className="sr-ride__section-title">Passengers</h3>
            <div className="sr-ride__passenger-avatars">
              {passengerList.map((el) => (
                <Link to={`/profile/${el.userID}`} className="sr-ride__passenger-avatar-link" key={el.userID} title={el.seatsBooked > 1 ? `${el.name} · ${el.seatsBooked} seats` : el.name}>
                  <img
                    src={el.avatar}
                    alt={el.name}
                    className="sr-ride__passenger-avatar-img"
                  />
                  {el.seatsBooked > 1 && (
                    <span className="sr-ride__passenger-seat-count">{el.seatsBooked}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── Confirm Arrival ── */}
        {rideDetails.isCompleted && !isPoster && isPassenger && (() => {
          const myRecord = passengerList.find(el => el.userID === loginUser._id);
          if (!myRecord) return null;
          if (myRecord.hasConfirmedCompletion === "pending") {
            // Code-era rides: the boarding code already did the confirming.
            // Verified passengers may confirm early (or just wait — silence
            // becomes confirmation after 24h); unverified ones can only dispute.
            if (rideDetails.usesCodes) {
              return (
                <div className="sr-ride__confirmation">
                  <h3 className="sr-ride__section-title">How was your ride?</h3>
                  {myRecord.attendanceStatus === "verified" ? (
                    <p className="sr-ride__confirmation-text">
                      Your boarding was verified — you're all set, and your arrival confirms automatically in 24 hours.
                      Confirm now to unlock your review sooner, or dispute if something went wrong.
                    </p>
                  ) : (
                    <p className="sr-ride__confirmation-text">
                      The driver never verified your boarding, so there's nothing to confirm.
                      If this ride did happen and something went wrong, you can dispute it.
                    </p>
                  )}
                  <div className="sr-ride__confirmation-actions">
                    {myRecord.attendanceStatus === "verified" && (
                      <button onClick={() => handleConfirmCompletion(true)} className="sr-ride__btn sr-ride__btn--book" disabled={confirmingCompletion}>
                        <FontAwesomeIcon icon={faCheck} /> Confirm arrival
                      </button>
                    )}
                    <button onClick={() => handleConfirmCompletion(false)} className="sr-ride__btn sr-ride__btn--danger" disabled={confirmingCompletion}>
                      <FontAwesomeIcon icon={faXmark} /> Dispute
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div className="sr-ride__confirmation">
                <h3 className="sr-ride__section-title">Confirm your arrival</h3>
                <p className="sr-ride__confirmation-text">Did you arrive safely at your destination?</p>
                <div className="sr-ride__confirmation-actions">
                  <button onClick={() => handleConfirmCompletion(true)} className="sr-ride__btn sr-ride__btn--book" disabled={confirmingCompletion}>
                    <FontAwesomeIcon icon={faCheck} /> Yes, Confirmed
                  </button>
                  <button onClick={() => handleConfirmCompletion(false)} className="sr-ride__btn sr-ride__btn--danger" disabled={confirmingCompletion}>
                    <FontAwesomeIcon icon={faXmark} /> No, Dispute
                  </button>
                </div>
              </div>
            );
          }
          if (myRecord.hasConfirmedCompletion === "confirmed") {
            return (
              <div className="sr-ride__confirmation-badge sr-ride__confirmation-badge--confirmed">
                <FontAwesomeIcon icon={faCheck} /> Arrival confirmed
              </div>
            );
          }
          if (myRecord.hasConfirmedCompletion === "disputed") {
            return (
              <div className="sr-ride__confirmation-badge sr-ride__confirmation-badge--disputed">
                <FontAwesomeIcon icon={faXmark} /> You disputed this ride
              </div>
            );
          }
          if (myRecord.hasConfirmedCompletion === "unconfirmed") {
            return (
              <div className="sr-ride__confirmation-badge sr-ride__confirmation-badge--disputed">
                <FontAwesomeIcon icon={faXmark} /> Confirmation window passed
              </div>
            );
          }
          return null;
        })()}

        {/* ── Review ── */}
        {/* Only actual passengers can review the poster (the server enforces
            this too — rendering the form to every viewer just invited 400s).
            On code-bearing rides the review unlocks once the driver has
            verified this passenger's boarding code. */}
        {rideDetails.isCompleted && !isPoster && isPassenger && (() => {
          if (myRideReviews[poster._id] !== undefined) {
            return (
              <div className="sr-ride__review">
                <h3 className="sr-ride__section-title">Rate this ride</h3>
                <div className="sr-ride__availed-reviewed">You rated this ride ({myRideReviews[poster._id]}★)</div>
              </div>
            );
          }
          const myRecord = passengerList.find((el) => el.userID === loginUser._id);
          if (myRecord?.hasBoardingCode && myRecord?.attendanceStatus !== "verified") {
            return (
              <div className="sr-ride__review">
                <h3 className="sr-ride__section-title">Rate this ride</h3>
                <p className="sr-ride__confirmation-text">Reviews unlock once the driver has verified your boarding code.</p>
              </div>
            );
          }
          return (
            <div className="sr-ride__review">
              <h3 className="sr-ride__section-title">Rate this ride</h3>
              <div className="sr-ride__review-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`sr-ride__review-star ${star <= reviewRating ? 'sr-ride__review-star--filled' : 'sr-ride__review-star--empty'}`}
                    onClick={() => setReviewRating(star)}
                  >
                    ★
                  </span>
                ))}
              </div>
              <textarea
                className="sr-ride__textarea"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Write a comment (optional)"
                maxLength={500}
              />
              <button
                className="sr-ride__submit-btn"
                onClick={async () => {
                  if (reviewRating === 0) { toast.error("Please select a rating."); return; }
                  try {
                    await axios.post("/api/v1/review/create", { rideId: _id, revieweeId: poster._id, rating: reviewRating, comment: reviewComment });
                    toast.success("Review submitted!");
                    setMyRideReviews((prev) => ({ ...prev, [poster._id]: reviewRating }));
                    setReviewRating(0);
                    setReviewComment("");
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to submit review.");
                  }
                }}
              >Submit Review</button>
            </div>
          );
        })()}

        {/* ── Ride Actions ── */}
        <div className="sr-ride__action-bar" data-tour="ride-actions">
          <div className="sr-ride__action-bar-inner">
            {!isPoster && (
              <>
                {isImportedRide && (
                  <>
                    {/* The poster of an unclaimed import isn't on SastiRide yet, so
                        there's no in-app chat to reach them — WhatsApp is the only
                        way. Shown to anyone who can see the ride (the server gates a
                        private group's rides to its members). The button vanishes
                        the moment the poster joins and verifies: the ride becomes
                        claimed, isImportedRide flips false, and contact moves to
                        in-app chat. `waNumber` is empty for @lid posts with no
                        recoverable number, so the button hides itself there. */}
                    {waNumber && (
                      <button onClick={openWhatsAppContact} className="sr-ride__btn sr-ride__btn--whatsapp-contact">
                        <FontAwesomeIcon icon={faWhatsapp}/> Contact on WhatsApp
                      </button>
                    )}
                    {/* Admins can fix wrongly-parsed details (to/from, number) on imported rides. */}
                    {isAdmin(loginUser) && !isStarted && (
                      <button onClick={() => navigate(`/publish?edit=${_id}`)} className="sr-ride__btn sr-ride__btn--edit">
                        <FontAwesomeIcon icon={faPenToSquare}/> Edit
                      </button>
                    )}
                  </>
                )}

                {!isImportedRide && isOfferOrRequest && totalSeats > 0 && !isPassenger && !isStarted && !rideDetails.isCompleted && !blockedByWomenOnly &&
                  <div className="sr-ride__book-group">
                    {totalSeats > 1 && (
                      <select
                        className="sr-ride__seat-select"
                        value={Math.min(bookSeats, totalSeats)}
                        onChange={(e) => setBookSeats(Number(e.target.value))}
                        aria-label="Number of seats"
                      >
                        {Array.from({ length: totalSeats }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n} seat{n > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    )}
                    <button onClick={() => handleBookRide(_id)} className="sr-ride__btn sr-ride__btn--book">
                      <FontAwesomeIcon icon={faCircleCheck}/> Book{fare.amount ? ` · ${fare.currency || "PKR"} ${fare.amount * Math.min(bookSeats, totalSeats)}` : ""}
                    </button>
                  </div>
                }

                {!isImportedRide && isOfferOrRequest && totalSeats > 0 && !isPassenger && !isStarted && !rideDetails.isCompleted && blockedByWomenOnly &&
                  <span className="sr-ride__women-only-note">Reserved for women only</span>
                }

                {/* Ask the driver before booking. 1:1 thread, separate from the
                    ride group chat — opens (or reopens) it and jumps to it. */}
                {!isImportedRide && isOfferOrRequest && !isPassenger && !isStarted && !rideDetails.isCompleted &&
                  <button
                    onClick={async () => {
                      if (!isLoggedIn) { navigate("/login"); return; }
                      try {
                        const { data } = await axios.post(`/api/v1/inquiries/ride/${_id}/thread`);
                        navigate(`/inbox/inquiry/${data.thread.inquiryId}`);
                      } catch (err) {
                        toast.error(err.response?.data?.message || "Couldn't open the conversation.");
                      }
                    }}
                    className="sr-ride__btn sr-ride__btn--message"
                  >
                    <FontAwesomeIcon icon={faComment}/> Message the driver
                  </button>
                }

                {/* Requests: any driver can accept — creates their ride at the
                    requester's fare and books the requester onto it. */}
                {!isImportedRide && !isOfferOrRequest && !isStarted && !rideDetails.isCompleted && new Date(estimatedDate) > new Date() &&
                  <button onClick={() => handleAcceptRequest(_id)} className="sr-ride__btn sr-ride__btn--book">
                    <FontAwesomeIcon icon={faCar}/> I can take you{fare?.amount ? ` · ${fare.currency || "PKR"} ${fare.amount}` : ""}
                  </button>
                }

                {isOfferOrRequest && isPassenger && !isStarted &&
                  <button onClick={() => handleCancelBooking(_id, loginUser._id)} className="sr-ride__btn sr-ride__btn--danger">
                    <FontAwesomeIcon icon={faCircleXmark}/> Unbook
                  </button>
                }

                {isOfferOrRequest && rideDetails.isCompleted && isPassenger && (
                  (() => {
                    const myRecord = passengerList.find(el => el.userID === loginUser._id);
                    if (myRecord && myRecord.paymentStatus === "paid") {
                      return <span className="sr__payment-badge sr__payment-badge--paid">Paid</span>;
                    }
                    return (
                      <button disabled className="sr-ride__btn sr-ride__btn--pay">
                        <FontAwesomeIcon icon={faCreditCard}/> Pay Now
                      </button>
                    );
                  })()
                )}
              </>
            )}

            {isPoster && (
              <>
                {!isStarted && (
                  <>
                    <button onClick={() => navigate(`/publish?edit=${_id}`)} className="sr-ride__btn sr-ride__btn--edit">
                      <FontAwesomeIcon icon={faPenToSquare}/> Edit
                    </button>
                    {isOfferOrRequest && (
                      <button onClick={() => onBtnClickStartRide(_id)} className="sr-ride__btn sr-ride__btn--start">
                        <FontAwesomeIcon icon={faPlay}/> Start
                      </button>
                    )}
                    <button onClick={() => onBtnClickDeleteRide(_id)} className="sr-ride__btn sr-ride__btn--danger">
                      <FontAwesomeIcon icon={faTrashCan}/> Delete
                    </button>
                  </>
                )}

                {isStarted && !rideDetails.isCompleted && isOfferOrRequest && (
                  <>
                    {/* Complete unlocks only once every passenger is resolved
                        (code verified or marked no-show) — server enforces too. */}
                    {unresolvedBoarding === 0 ? (
                      <button onClick={() => onBtnClickCompleteRide(_id)} className="sr-ride__btn sr-ride__btn--book">
                        <FontAwesomeIcon icon={faCircleCheck}/> Complete
                      </button>
                    ) : (
                      <button disabled className="sr-ride__btn sr-ride__btn--book" title="Enter each passenger's boarding code, or mark no-shows, to unlock Complete">
                        <FontAwesomeIcon icon={faCircleCheck}/> Verify {unresolvedBoarding} passenger{unresolvedBoarding === 1 ? "" : "s"} to complete
                      </button>
                    )}
                    {/* Mis-tap escape hatch: server allows undo for 10 min after Start. */}
                    {rideDetails.startedAt && (Date.now() - new Date(rideDetails.startedAt).getTime()) < 10 * 60 * 1000 && (
                      <button onClick={() => onBtnClickUnstartRide(_id)} className="sr-ride__btn sr-ride__btn--edit">
                        <FontAwesomeIcon icon={faRotate}/> Undo start
                      </button>
                    )}
                  </>
                )}

                {rideDetails.isCompleted && (
                  <button onClick={() => navigate(`/publish?repeat=${_id}`)} className="sr-ride__btn sr-ride__btn--edit">
                    <FontAwesomeIcon icon={faRotate}/> Repeat
                  </button>
                )}
              </>
            )}

            <button onClick={() => onBtnClickLikeRide(_id)} className={`sr-ride__btn sr-ride__btn--like ${isLikedByUser ? 'is-liked' : ''}`}>
              {Array.isArray(likes) ? likes.length : 0} <FontAwesomeIcon icon={faBookmark}/>
            </button>

            <button
              onClick={handleShareRide}
              className="sr-ride__btn sr-ride__btn--whatsapp"
              title="Share this ride"
            >
              <FontAwesomeIcon icon={faShareNodes}/>
            </button>

            {!isPoster && (
              <button onClick={() => setShowReportForm(!showReportForm)} className="sr-ride__btn sr-ride__btn--report" title="Report">
                <FontAwesomeIcon icon={faFlag}/>
              </button>
            )}
          </div>

          {/* ── Safety row: share trip + SOS for anyone on this ride ── */}
          {(isPoster || isPassenger) && !rideDetails.isCompleted && (
            <div className="sr-ride__safety-bar">
              <button onClick={handleShareTrip} className="sr-ride__btn sr-ride__btn--share-trip">
                <FontAwesomeIcon icon={faShareNodes}/> Share my trip
              </button>
              <button onClick={handleSos} className="sr-ride__btn sr-ride__btn--sos">
                <FontAwesomeIcon icon={faTriangleExclamation}/> SOS
              </button>
            </div>
          )}
        </div>

        {isPoster && (isStarted || rideDetails.isCompleted) && isOfferOrRequest && passengerList.length > 0 && (
              <div className="sr-ride__availed-section">
                <h4 className="sr-ride__availed-title">
                  {rideDetails.isCompleted
                    ? "Mark attendance & review"
                    : `Verify boarding — ${passengerList.length - unresolvedBoarding} of ${passengerList.length} done`}
                </h4>
                {!rideDetails.isCompleted && unresolvedBoarding > 0 && (
                  <p className="sr-ride__confirmation-text">
                    Ask each passenger for their boarding code as they get in. The Complete button unlocks once everyone is verified or marked a no-show.
                  </p>
                )}
                <div className="sr-ride__availed-list">
                  {passengerList.map((el) => {
                    const pr = passengerReviews[el.userID] || { rating: 0, comment: "", submitted: false };
                    // Server-known review (survives refresh) or this session's optimistic one.
                    const reviewedRating = pr.submitted ? pr.rating : myRideReviews[el.userID];
                    return (
                      <div className="sr-ride__availed-row sr-ride__availed-row--expanded" key={el.userID}>
                        <div className="sr-ride__availed-row-top">
                          <Link to={`/profile/${el.userID}`} className="sr-ride__availed-user">
                            <img src={el.avatar} alt={el.name} className="sr-ride__availed-avatar" />
                            <span className="sr-ride__availed-name">{el.name}</span>
                          </Link>
                          {el.hasBoardingCode ? (
                            el.attendanceStatus === "verified" ? (
                              <span className="sr-ride__availed-toggle sr-ride__availed-toggle--yes">
                                <FontAwesomeIcon icon={faCheck} /> Boarding verified
                              </span>
                            ) : (
                              <button
                                onClick={() => handleToggleAvailed(_id, el.userID)}
                                className={`sr-ride__availed-toggle ${el.attendanceStatus === "no_show" ? 'sr-ride__availed-toggle--no' : 'sr-ride__availed-toggle--neutral'}`}
                                title={el.attendanceStatus === "no_show" ? "Marked as no-show — tap to undo" : "Mark as no-show"}
                              >
                                <FontAwesomeIcon icon={faXmark} />
                                {el.attendanceStatus === "no_show" ? " No-show (undo)" : " Didn't show"}
                              </button>
                            )
                          ) : (
                            <button
                              onClick={() => handleToggleAvailed(_id, el.userID)}
                              className={`sr-ride__availed-toggle ${el.isRideAvailed ? 'sr-ride__availed-toggle--yes' : 'sr-ride__availed-toggle--no'}`}
                              title={el.isRideAvailed ? "Showed up" : "Didn't show up"}
                            >
                              <FontAwesomeIcon icon={el.isRideAvailed ? faCheck : faXmark} />
                              {el.isRideAvailed ? " Showed up" : " Didn't show"}
                            </button>
                          )}
                        </div>
                        {/* Code entry: the passenger tells the driver this code at pickup. */}
                        {el.hasBoardingCode && el.attendanceStatus !== "verified" && (
                          <div className="sr-ride__confirmation-actions">
                            <input
                              type="text"
                              className="sr-ride__code-input"
                              value={boardingInputs[el.userID] || ""}
                              onChange={(e) => setBoardingInputs((prev) => ({ ...prev, [el.userID]: e.target.value.toUpperCase() }))}
                              placeholder="Boarding code (SR-XXXXXX)"
                              maxLength={12}
                            />
                            <button onClick={() => handleVerifyBoarding(_id, el.userID)} className="sr-ride__btn sr-ride__btn--book">
                              <FontAwesomeIcon icon={faCheck} /> Verify
                            </button>
                          </div>
                        )}
                        {rideDetails.isCompleted && (
                        reviewedRating !== undefined ? (
                          <div className="sr-ride__availed-reviewed">Reviewed ({reviewedRating}★)</div>
                        ) : el.hasBoardingCode && el.attendanceStatus !== "verified" ? (
                          <div className="sr-ride__availed-reviewed">Verify boarding to review this passenger</div>
                        ) : (
                          <div className="sr-ride__availed-review">
                            <div className="sr-ride__review-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={`sr-ride__review-star ${star <= pr.rating ? 'sr-ride__review-star--filled' : 'sr-ride__review-star--empty'}`}
                                  onClick={() => setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], rating: star } }))}
                                >★</span>
                              ))}
                            </div>
                            <textarea
                              className="sr-ride__textarea sr-ride__textarea--small"
                              value={pr.comment}
                              onChange={(e) => setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], rating: pr.rating, comment: e.target.value } }))}
                              placeholder="Comment (optional)"
                              maxLength={500}
                            />
                            <button
                              className="sr-ride__submit-btn sr-ride__submit-btn--small"
                              onClick={async () => {
                                if (pr.rating === 0) { toast.error("Please select a rating."); return; }
                                try {
                                  await axios.post("/api/v1/review/create", { rideId: _id, revieweeId: el.userID, rating: pr.rating, comment: pr.comment || "" });
                                  setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], submitted: true } }));
                                  toast.success(`Review for ${el.name} submitted!`);
                                } catch (err) {
                                  toast.error(err.response?.data?.message || "Failed to submit review.");
                                }
                              }}
                            >Submit</button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

        {/* ── How it works (role-aware, shown while it's still actionable) ── */}
        {isPoster && !isStarted && !rideDetails.isCompleted && isOfferOrRequest && (
          <ul className="sr-ride__delete-rules">
            <li>Edit or delete anytime before you start. Deleting is free unless it's within 24 hours of departure with booked passengers — then it counts on your profile.</li>
            <li>Never started it? It expires quietly 6 hours after departure — no penalty, and your posting slot frees up.</li>
            <li>Each passenger gets a boarding code when they book. At pickup, ask for everyone's code and type them in (or mark no-shows) — the Complete button unlocks once all are resolved. Started by mistake? You have 10 minutes to undo.</li>
            <li>Complete the ride when you arrive — if you forget, we'll complete it for you 24 hours after departure.</li>
            <li>Passengers are notified of every change: edits, start, completion, or cancellation.</li>
          </ul>
        )}
        {isPoster && !isStarted && !rideDetails.isCompleted && !isOfferOrRequest && (
          <ul className="sr-ride__delete-rules">
            <li>Your request shows drivers the fare you're offering. Any driver can tap "I can take you" — you'll be booked onto their ride automatically and notified.</li>
            <li>You can edit or delete it anytime; unanswered requests expire quietly 6 hours after their departure time.</li>
          </ul>
        )}
        {!isPoster && !isImportedRide && isOfferOrRequest && !isStarted && !rideDetails.isCompleted && (
          <ul className="sr-ride__delete-rules">
            <li>Unbooking is free until 24 hours before departure. Later unbooks leave the driver a seat they can't refill, so they count on your profile.</li>
            <li>Booking gives you a personal boarding code — tell it to the driver at pickup. That single step verifies your ride and unlocks your review; nothing to do afterwards.</li>
            <li>Something wrong? You can always dispute a completed ride — no code needed, and our admins are alerted.</li>
          </ul>
        )}

        {/* ── Report Form (expands below actions) ── */}
        {!isPoster && showReportForm && (
          <div className="sr-ride__report-form">
            <select className="sr-ride__select" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment</option>
              <option value="inappropriate">Inappropriate</option>
              <option value="fraud">Fraud</option>
              <option value="safety">Safety Concern</option>
              <option value="other">Other</option>
            </select>
            <textarea
              className="sr-ride__textarea"
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Describe the issue (optional)"
              maxLength={1000}
            />
            <button
              className="sr-ride__submit-btn"
              onClick={async () => {
                try {
                  await axios.post("/api/v1/report/create", { reportedRide: _id, reportedUser: poster._id, reason: reportReason, description: reportDescription });
                  toast.success("Report submitted. Thank you.");
                  setShowReportForm(false);
                  setReportDescription("");
                } catch (err) {
                  toast.error(err.response?.data?.message || "Failed to submit report.");
                }
              }}
            >Submit Report</button>
          </div>
        )}
      </div>
      <TourButton onClick={startTour} />
    </div>
  );
}

export default Ride;
